import type { ChatMessage } from "./types.js";

/** A rendered row of a conversation: a day separator, the unread divider, or a message with its group position (D67). */
export type ConversationItem =
  | { kind: "day"; key: string; label: "today" | "yesterday" | string }
  | { kind: "unread"; key: string; n: number }
  | { kind: "message"; key: string; message: ChatMessage; own: boolean; first: boolean; last: boolean };

/** Consecutive messages from one author within this window share one group (name once, tight bubbles). */
export const GROUP_WINDOW_MS = 3 * 60_000;
const DAY_MS = 86_400_000;
const PREVIEW_CHARS = 60;

/** Calendar day in the game's time zone as yyyy-mm-dd. */
export function dayKey(ts: number): string {
  return new Date(ts).toLocaleDateString("sv-SE", { timeZone: "Europe/Berlin" });
}

export function dayLabel(ts: number, now: number): "today" | "yesterday" | string {
  const key = dayKey(ts);
  if (key === dayKey(now)) return "today";
  if (key === dayKey(now - DAY_MS)) return "yesterday";
  return key;
}

/** List-row stamp: clock time today, "yesterday" for yesterday, else the calendar day. */
export function stampLabel(ts: number, now: number): { kind: "time"; text: string } | { kind: "yesterday" } | { kind: "date"; text: string } {
  const label = dayLabel(ts, now);
  if (label === "today") return { kind: "time", text: clockTime(ts) };
  if (label === "yesterday") return { kind: "yesterday" };
  return { kind: "date", text: label.slice(5).split("-").reverse().join(".") };
}

export function clockTime(ts: number): string {
  return new Date(ts).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" });
}

function sameGroup(a: ChatMessage, b: ChatMessage): boolean {
  return a.accountId === b.accountId && a.system === b.system && Math.abs(b.createdAt - a.createdAt) <= GROUP_WINDOW_MS && dayKey(a.createdAt) === dayKey(b.createdAt);
}

/**
 * Day separators, author groups and one unread divider before the last `unread` messages from other players.
 * `unread` is the server's count for the channel at the moment it was opened; own messages never count.
 */
export function buildConversation(messages: ChatMessage[], accountId: string, unread: number, now: number): ConversationItem[] {
  let divider = -1;
  if (unread > 0) {
    let seen = 0;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]!.accountId === accountId) continue;
      seen++;
      divider = i;
      if (seen === unread) break;
    }
  }
  const items: ConversationItem[] = [];
  let lastDay: string | null = null;
  messages.forEach((m, i) => {
    const day = dayKey(m.createdAt);
    const prev = messages[i - 1];
    const next = messages[i + 1];
    if (day !== lastDay) {
      items.push({ kind: "day", key: `day-${day}`, label: dayLabel(m.createdAt, now) });
      lastDay = day;
    }
    const afterDivider = i === divider;
    if (afterDivider) items.push({ kind: "unread", key: "unread", n: unread });
    const first = !prev || afterDivider || !sameGroup(prev, m);
    const last = !next || i + 1 === divider || !sameGroup(m, next);
    items.push({ kind: "message", key: m.id, message: m, own: m.accountId === accountId, first, last });
  });
  return items;
}

/** One line for the conversation list: the text, or the card's label, cut to 60 characters. */
export function conversationPreview(m: ChatMessage | undefined, labels: { battle: string; unit: string; deleted: string }): string {
  if (!m) return "";
  if (m.deleted) return labels.deleted;
  const text = m.text.trim();
  if (text) return text.length > PREVIEW_CHARS ? `${text.slice(0, PREVIEW_CHARS)}…` : text;
  if (m.card?.kind === "battle") return `⚔ ${labels.battle}`;
  if (m.card?.kind === "unit") return `★ ${labels.unit}`;
  if (m.card?.kind === "coords") return `⌖ ${m.card.label} · ${m.card.x},${m.card.y}`;
  return "";
}
